import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import memoService, { Memo, CreateMemoData, MemoShare, ShareMemoData } from '../services/memoService';
import MemoCard from '../components/memo/MemoCard';
import MemoForm from '../components/memo/MemoForm';
import ShareMemoModal from '../components/memo/ShareMemoModal';
import {
  Plus, Grid, List, Archive, Search, X, Bell
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';

// Grid configuration
const GRID_CONFIG = {
  CARD_WIDTH: 280,
  CARD_HEIGHT: 200,
  GAP: 20,
  COLUMNS: 4, // Default columns, will be responsive
  MIN_COLUMNS: 2,
  MAX_COLUMNS: 6
};

// Types for Google Keep features
type ViewMode = 'grid' | 'list' | 'compact';
type FilterMode = 'all' | 'pinned' | 'reminders' | 'labels' | 'images' | 'checklists';
type SortMode = 'updated' | 'created' | 'title' | 'reminder' | 'color';

export default function MyMemoPage() {
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [memos, setMemos] = useState<Memo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // View and filter state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [sortMode, setSortMode] = useState<SortMode>('updated');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  // Sharing state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedMemoForSharing, setSelectedMemoForSharing] = useState<Memo | null>(null);
  const [memoShares, setMemoShares] = useState<Record<number, MemoShare[]>>({});

  // Grid state
  const [memoOrder, setMemoOrder] = useState<number[]>([]);
  const boardRef = useRef<HTMLDivElement>(null);

  // Advanced features (for future implementation)
  const [reminderFilter, setReminderFilter] = useState<'all' | 'today' | 'tomorrow' | 'week'>('all');

  // Drag state
  const [draggedMemoId, setDraggedMemoId] = useState<number | null>(null);

  // Save memo order to local storage whenever it changes
  useEffect(() => {
    if (memoOrder.length > 0) {
      localStorage.setItem('memoBoard_order', JSON.stringify(memoOrder));
    }
  }, [memoOrder]);



  // Load memo order from local storage after memos are loaded
  useEffect(() => {
    if (memos.length > 0) {
      const savedOrder = localStorage.getItem('memoBoard_order');

      if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);

        // Only set order if it contains valid memo IDs
        const validOrder = parsedOrder.filter((id: number) =>
          memos.some(memo => memo.id === id)
        );

        if (validOrder.length > 0) {
          setMemoOrder(parsedOrder); // Keep the full order, including potentially new memos
        }
      }
    }
  }, [memos]);

  // Available colors for sticky notes
  const availableColors = [
    'yellow', 'blue', 'green', 'pink', 'purple', 'orange',
    'red', 'teal', 'indigo', 'cyan', 'lime', 'rose',
    'amber', 'emerald', 'violet', 'sky'
  ];

  // Fetch memos on component mount
  useEffect(() => {
    fetchMemos();
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCreating) {
        setIsCreating(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isCreating]);

  // Function to fetch memos
  const fetchMemos = async () => {
    try {
      setIsLoading(true);
      const fetchedMemos = await memoService.getUserMemos();
      setMemos(fetchedMemos);
      setError(null);
    } catch (err) {
      console.error('Error fetching memos:', err);
      setError(t('pages.myMemo.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create a new memo
  const handleCreateMemo = async (memoData: CreateMemoData) => {
    try {
      const newMemo = await memoService.createMemo(memoData);

      // Add new memo to the beginning of the list
      const updatedMemos = [newMemo, ...memos];
      setMemos(updatedMemos);

      // Update memo order to include the new memo at the beginning
      setMemoOrder([newMemo.id, ...memoOrder]);

      setIsCreating(false);
    } catch (err) {
      console.error('Error creating memo:', err);
      setError(t('pages.myMemo.createError'));
    }
  };

  // Function to update a memo
  const handleUpdateMemo = async (id: number, updatedMemo: Partial<Memo>) => {
    try {
      const updated = await memoService.updateMemo(id, updatedMemo);
      setMemos(memos.map(memo => memo.id === id ? updated : memo));
    } catch (err) {
      console.error('Error updating memo:', err);
      setError(t('pages.myMemo.updateError'));
    }
  };

  // Function to delete a memo
  const handleDeleteMemo = async (id: number) => {
    try {
      await memoService.deleteMemo(id);
      setMemos(memos.filter(memo => memo.id !== id));
      // Remove from memo order as well
      setMemoOrder(memoOrder.filter(memoId => memoId !== id));
    } catch (err) {
      console.error('Error deleting memo:', err);
      setError(t('pages.myMemo.deleteError'));
    }
  };

  // Sharing handler functions
  const handleOpenShareModal = async (memo: Memo) => {
    setSelectedMemoForSharing(memo);

    // Fetch existing shares if user is owner
    if (memo.accessLevel === 'owner') {
      try {
        const shares = await memoService.getMemoShares(memo.id);
        setMemoShares(prev => ({ ...prev, [memo.id]: shares }));
      } catch (err) {
        console.error('Error fetching memo shares:', err);
      }
    }

    setShareModalOpen(true);
  };

  const handleShare = async (shares: ShareMemoData[]) => {
    if (!selectedMemoForSharing) return;

    try {
      const newShares = await memoService.shareMemo(selectedMemoForSharing.id, shares);
      setMemoShares(prev => ({
        ...prev,
        [selectedMemoForSharing.id]: [...(prev[selectedMemoForSharing.id] || []), ...newShares]
      }));

      // Refresh memos to update shared status
      await fetchMemos();
    } catch (err) {
      console.error('Error sharing memo:', err);
      setError('Failed to share memo');
    }
  };

  const handleRemoveShare = async (userId: number) => {
    if (!selectedMemoForSharing) return;

    try {
      await memoService.removeMemoShare(selectedMemoForSharing.id, userId);
      setMemoShares(prev => ({
        ...prev,
        [selectedMemoForSharing.id]: (prev[selectedMemoForSharing.id] || []).filter(s => s.userId !== userId)
      }));

      // Refresh memos to update shared status
      await fetchMemos();
    } catch (err) {
      console.error('Error removing share:', err);
      setError('Failed to remove share');
    }
  };

  const handleUpdateShareAccess = async (userId: number, accessLevel: 'view' | 'edit') => {
    if (!selectedMemoForSharing) return;

    try {
      await memoService.updateMemoShareAccess(selectedMemoForSharing.id, userId, accessLevel);
      setMemoShares(prev => ({
        ...prev,
        [selectedMemoForSharing.id]: (prev[selectedMemoForSharing.id] || []).map(s =>
          s.userId === userId ? { ...s, accessLevel } : s
        )
      }));
    } catch (err) {
      console.error('Error updating share access:', err);
      setError('Failed to update share access');
    }
  };

  // Filter and sort memos
  const getFilteredAndSortedMemos = useCallback(() => {
    let filteredMemos = [...memos];

    // Apply archived filter (safely handle optional field)
    if (!showArchived) {
      filteredMemos = filteredMemos.filter(memo => !memo.isArchived);
    } else {
      filteredMemos = filteredMemos.filter(memo => memo.isArchived === true);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredMemos = filteredMemos.filter(memo =>
        memo.title.toLowerCase().includes(query) ||
        memo.content.toLowerCase().includes(query) ||
        (memo.labels && memo.labels.some(label => label.toLowerCase().includes(query)))
      );
    }

    // Apply label filter
    if (selectedLabels.length > 0) {
      filteredMemos = filteredMemos.filter(memo =>
        memo.labels && memo.labels.some(label => selectedLabels.includes(label))
      );
    }

    // Apply filter mode (safely handle optional fields)
    switch (filterMode) {
      case 'pinned':
        filteredMemos = filteredMemos.filter(memo => memo.isPinned === true);
        break;
      case 'reminders':
        filteredMemos = filteredMemos.filter(memo => memo.reminder);
        break;
      case 'labels':
        filteredMemos = filteredMemos.filter(memo => memo.labels && memo.labels.length > 0);
        break;
      case 'images':
        filteredMemos = filteredMemos.filter(memo => memo.images && memo.images.length > 0);
        break;
      case 'checklists':
        filteredMemos = filteredMemos.filter(memo => memo.checklistItems && memo.checklistItems.length > 0);
        break;
      default:
        break;
    }

    // Apply reminder filter
    if (reminderFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      filteredMemos = filteredMemos.filter(memo => {
        if (!memo.reminder) return false;
        const reminderDate = new Date(memo.reminder);

        switch (reminderFilter) {
          case 'today':
            return reminderDate >= today && reminderDate < tomorrow;
          case 'tomorrow':
            return reminderDate >= tomorrow && reminderDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);
          case 'week':
            return reminderDate >= today && reminderDate <= nextWeek;
          default:
            return true;
        }
      });
    }

    // Sort memos
    switch (sortMode) {
      case 'created':
        filteredMemos.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'updated':
        filteredMemos.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'title':
        filteredMemos.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'reminder':
        filteredMemos.sort((a, b) => {
          if (!a.reminder && !b.reminder) return 0;
          if (!a.reminder) return 1;
          if (!b.reminder) return -1;
          return new Date(a.reminder).getTime() - new Date(b.reminder).getTime();
        });
        break;
      default:
        break;
    }

    // Apply grid ordering for grid view
    if (viewMode === 'grid' && memoOrder.length > 0) {
      const orderedMemos: Memo[] = [];
      const unorderedMemos: Memo[] = [];

      filteredMemos.forEach(memo => {
        const orderIndex = memoOrder.indexOf(memo.id);
        if (orderIndex !== -1) {
          orderedMemos[orderIndex] = memo;
        } else {
          unorderedMemos.push(memo);
        }
      });

      return [...orderedMemos.filter(Boolean), ...unorderedMemos];
    }

    return filteredMemos;
  }, [memos, memoOrder, searchQuery, selectedLabels, filterMode, reminderFilter, sortMode, showArchived, viewMode]);


  // Function to handle memo reordering
  const handleMemoReorder = useCallback((draggedMemoId: number, targetIndex: number) => {
    const currentMemos = getFilteredAndSortedMemos();
    const draggedIndex = currentMemos.findIndex(m => m.id === draggedMemoId);

    if (draggedIndex === -1 || draggedIndex === targetIndex) return;

    // Create new order array based on current memo order
    const newOrder = [...memoOrder];

    // Remove dragged memo from its current position
    const draggedOrderIndex = newOrder.indexOf(draggedMemoId);
    if (draggedOrderIndex !== -1) {
      newOrder.splice(draggedOrderIndex, 1);
    }

    // Find target memo and insert dragged memo before it
    const targetMemo = currentMemos[targetIndex];
    const targetOrderIndex = newOrder.indexOf(targetMemo.id);

    if (targetOrderIndex !== -1) {
      newOrder.splice(targetOrderIndex, 0, draggedMemoId);
    } else {
      // If target memo not in order, add dragged memo at the target position
      newOrder.splice(targetIndex, 0, draggedMemoId);
    }

    // Update memo order state
    setMemoOrder(newOrder);
  }, [memoOrder, getFilteredAndSortedMemos]);

  // Function to handle search
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // Drag and drop functions for reordering
  const handleDragStart = useCallback((e: React.DragEvent, memoId: number) => {
    e.dataTransfer.setData('text/plain', memoId.toString());
    e.dataTransfer.effectAllowed = 'move';
    setDraggedMemoId(memoId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const draggedMemoIdFromData = parseInt(e.dataTransfer.getData('text/plain'));

    if (!draggedMemoIdFromData) {
      setDraggedMemoId(null);
      return;
    }

    const orderedMemos = getFilteredAndSortedMemos();
    const draggedIndex = orderedMemos.findIndex((memo: Memo) => memo.id === draggedMemoIdFromData);

    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDraggedMemoId(null);
      return;
    }

    handleMemoReorder(draggedMemoIdFromData, targetIndex);
    setDraggedMemoId(null);
  }, [handleMemoReorder, getFilteredAndSortedMemos]);

  const handleDragEnd = useCallback(() => {
    setDraggedMemoId(null);
  }, []);

  const filteredMemos = getFilteredAndSortedMemos();

  if (!user) {
    return (
      <DashboardLayout>
        <Container>
          <div className="flex items-center justify-center h-64">
            <p className="text-lg">{t('pages.myMemo.loginRequired')}</p>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container className={`py-6 space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        <div className="flex flex-col h-full">
          {/* Header with title and actions */}
          <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:justify-between sm:items-center">
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              {t('pages.myMemo.title')}
            </h1>
          </div>

          {/* Enhanced Toolbar */}
          <div className={`mb-4 p-4 rounded-lg shadow-sm transition-colors ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Search Bar */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={t('pages.myMemo.searchPlaceholder')}
                  value={searchQuery}
                  onChange={handleSearch}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark'
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              <button
                onClick={() => setIsCreating(true)}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
              >
                <Plus size={20} />
                <span>{t('pages.myMemo.newMemo')}</span>
              </button>
            </div>

            {/* Filter and View Controls */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {/* View Mode Toggle */}
              <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 flex items-center space-x-1 ${viewMode === 'grid'
                    ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                    : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Grid size={16} />
                  <span className="text-sm">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 flex items-center space-x-1 ${viewMode === 'list'
                    ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                    : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <List size={16} />
                  <span className="text-sm">List</span>
                </button>
              </div>

              {/* Filter Dropdown */}
              <select
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value as FilterMode)}
                className={`px-3 py-1 rounded border text-sm ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="all">All Notes</option>
                <option value="pinned">Pinned</option>
                <option value="reminders">Reminders</option>
                <option value="labels">With Labels</option>
                <option value="images">With Images</option>
                <option value="checklists">Checklists</option>
              </select>

              {/* Sort Dropdown */}
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className={`px-3 py-1 rounded border text-sm ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="updated">Last Updated</option>
                <option value="created">Date Created</option>
                <option value="title">Title</option>
                <option value="reminder">Reminder Date</option>
              </select>

              {/* Archive Toggle */}
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`px-3 py-1 rounded flex items-center space-x-1 text-sm ${showArchived
                  ? theme === 'dark' ? 'bg-yellow-600 text-white' : 'bg-yellow-500 text-white'
                  : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <Archive size={16} />
                <span>{showArchived ? 'Show Active' : 'Show Archived'}</span>
              </button>

            </div>

            {/* Active Filters Display */}
            {(selectedLabels.length > 0 || reminderFilter !== 'all') && (
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-sm text-gray-500">Active filters:</span>
                {selectedLabels.map(label => (
                  <span
                    key={label}
                    className={`px-2 py-1 rounded-full text-xs flex items-center space-x-1 ${theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                      }`}
                  >
                    <span>{label}</span>
                    <button
                      onClick={() => setSelectedLabels(selectedLabels.filter(l => l !== label))}
                      className="hover:bg-blue-700 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {reminderFilter !== 'all' && (
                  <span
                    className={`px-2 py-1 rounded-full text-xs flex items-center space-x-1 ${theme === 'dark' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'
                      }`}
                  >
                    <Bell size={12} />
                    <span>{reminderFilter}</span>
                    <button
                      onClick={() => setReminderFilter('all')}
                      className="hover:bg-green-700 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className={`mb-4 rounded-md p-4 ${theme === 'dark'
              ? 'bg-red-900/30 text-red-200 border border-red-700'
              : 'bg-red-100 border border-red-400 text-red-700'
              }`} role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* Loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            /* Memo board */
            <div
              ref={boardRef}
              className={`relative w-full min-h-[600px] rounded-lg border-2 border-dashed overflow-auto p-4 ${theme === 'dark'
                ? 'bg-gray-800 border-gray-600'
                : 'bg-gray-50 border-gray-300'
                }`}
              onDragOver={handleDragOver}
            >
              {/* Empty state */}
              {filteredMemos.length === 0 && !isLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <div className={`p-6 rounded-lg text-center ${theme === 'dark' ? 'bg-gray-800/70' : 'bg-white/70'}`}>
                    <i className="fas fa-sticky-note text-4xl mb-4 text-yellow-500"></i>
                    <h3 className="text-xl font-semibold mb-2">
                      {searchQuery ? t('pages.myMemo.noMatchingMemos') : t('pages.myMemo.noMemos')}
                    </h3>
                    <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {searchQuery ? t('pages.myMemo.tryDifferentSearch') : t('pages.myMemo.createFirstMemo')}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => setIsCreating(true)}
                        className={`inline-flex items-center px-4 py-2 rounded-md ${theme === 'dark'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                      >
                        <Plus size={18} className="mr-1" />
                        {t('pages.myMemo.newMemo')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Memo layout - Grid or List view */}
              {viewMode === 'grid' ? (
                <div
                  className="grid gap-4 auto-rows-min"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONFIG.CARD_WIDTH}px, 1fr))`,
                  }}
                >
                  {filteredMemos.map((memo, index) => {
                    return (
                      <div
                        key={memo.id}
                        className={`relative cursor-move transition-opacity ${draggedMemoId === memo.id ? 'opacity-50' : 'opacity-100'
                          }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, memo.id)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragOver={handleDragOver}
                      >
                        <MemoCard
                          memo={memo}
                          onUpdate={handleUpdateMemo}
                          onDelete={handleDeleteMemo}
                          onPositionChange={async () => { }} // No-op for grid mode
                          onShare={handleOpenShareModal}
                          viewMode="grid"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMemos.map((memo, index) => {
                    return (
                      <div
                        key={memo.id}
                        className={`flex items-start p-4 rounded-lg cursor-move transition-opacity ${draggedMemoId === memo.id ? 'opacity-50' : 'opacity-100'
                          } ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                          } shadow-sm border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                          }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, memo.id)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragOver={handleDragOver}
                      >
                        <div className="flex-1">
                          <MemoCard
                            memo={memo}
                            onUpdate={handleUpdateMemo}
                            onDelete={handleDeleteMemo}
                            onPositionChange={async () => { }} // No-op for list mode
                            onShare={handleOpenShareModal}
                            viewMode="list"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Create memo modal - Revamped UI/UX */}
          {isCreating && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200"
              onClick={(e) => {
                // Close modal when clicking outside
                if (e.target === e.currentTarget) {
                  setIsCreating(false);
                }
              }}
            >
              <div
                className={`w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl transform transition-all animate-in zoom-in-95 duration-300 ${theme === 'dark'
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-gray-100'
                  }`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header - Compact and Clean */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                  }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-blue-600/20' : 'bg-blue-50'
                      }`}>
                      <Plus className={`${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} size={20} />
                    </div>
                    <div>
                      <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                        {t('pages.myMemo.createNewMemo')}
                      </h2>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Create a note or checklist to keep track of your thoughts
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsCreating(false)}
                    className={`p-2 rounded-lg transition-all hover:scale-110 ${theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                    aria-label="Close dialog"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Content Area - Scrollable */}
                <div className={`overflow-y-auto px-6 py-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                  }`} style={{ maxHeight: 'calc(90vh - 140px)' }}>
                  <MemoForm
                    availableColors={availableColors}
                    onSubmit={handleCreateMemo}
                    onCancel={() => setIsCreating(false)}
                    supportChecklist={true}
                  />
                </div>

                {/* Footer - Subtle Helper Text */}
                <div className={`px-6 py-3 border-t ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'
                  }`}>
                  <div className="flex items-center justify-between">
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                      💡 Tip: Press <kbd className={`px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                        } text-xs font-mono`}>Esc</kbd> to close
                    </p>
                    <div className="flex items-center space-x-2">
                      <div className={`flex items-center space-x-1 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        <span>Auto-save enabled</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>

      {/* Share Memo Modal */}
      {shareModalOpen && selectedMemoForSharing && (
        <ShareMemoModal
          memoId={selectedMemoForSharing.id}
          memoTitle={selectedMemoForSharing.title}
          existingShares={memoShares[selectedMemoForSharing.id] || []}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedMemoForSharing(null);
          }}
          onShare={handleShare}
          onRemoveShare={handleRemoveShare}
          onUpdateAccessLevel={handleUpdateShareAccess}
        />
      )}
    </DashboardLayout>
  );
}
